import { SMTFoodEntryData } from '@equip.health/ui';

import { AnalyticsContextType } from '~/lib/contexts/AnalyticsContext';
import { DatadogContextType } from '~/lib/types/datadog/datadog';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import fileUtil, { isBlobOrDataUrl } from '~/lib/util/file.util';

import {
  compressImageWithAnalytics,
  CompressionErrorContext,
} from '~/components/maud/patients/profile/smt/hooks/compressImageWithAnalytics.util';
import {
  getSMTImageAction,
  SMTImageAction,
} from '~/components/maud/patients/profile/smt/hooks/useSMTImageAction';

interface PrepareSMTImageForUploadParams {
  data: SMTFoodEntryData;
  errorContext: CompressionErrorContext;
  logError: DatadogContextType['logError'];
  source: AnalyticsSourceValues;
  trackEvent: AnalyticsContextType['trackEvent'];
}

/**
 * Prepares an SMT image file for upload by validating, converting, and compressing it.
 * Handles blob/data URL validation, file conversion, and compression.
 *
 * @param params - Configuration object containing data and tracking utilities
 * @returns Promise that resolves to FormData with compressed file, or null if no upload is needed
 */
const prepareSMTImageForUpload = async ({
  data,
  errorContext,
  logError,
  source,
  trackEvent,
}: PrepareSMTImageForUploadParams): Promise<FormData | null> => {
  if (!data.image?.imageUrl) {
    return null;
  }

  const imageUrl = data.image.imageUrl;
  if (!isBlobOrDataUrl(imageUrl)) {
    return null;
  }

  const imageFile = await fileUtil.urlToFile(
    imageUrl,
    data.image.name || 'image.jpg',
  );

  if (!imageFile) {
    logError(new Error('Failed to convert image URL to file'), {
      ...errorContext,
      context: 'SMT image URL to file conversion failed',
      imageUrl,
    });
    return null;
  }

  const compressedFile = await compressImageWithAnalytics({
    errorContext,
    imageFile,
    logError,
    source,
    trackEvent,
  });

  const formData = new FormData();
  formData.append('file', compressedFile);

  return formData;
};

interface UploadSMTImageParams {
  data: SMTFoodEntryData;
  entryId: string;
  errorContext: CompressionErrorContext;
  existingEntry?: SMTEntry;
  logError: DatadogContextType['logError'];
  patientId: string;
  replaceSMTEntryImage?: (params: {
    formData: FormData;
    imageId: string;
    patientId: string;
    smtEntryId: string;
  }) => { unwrap: () => Promise<unknown> };
  source: AnalyticsSourceValues;
  trackEvent: AnalyticsContextType['trackEvent'];
  uploadSMTEntryImage: (params: {
    formData: FormData;
    patientId: string;
    smtEntryId: string;
  }) => { unwrap: () => Promise<unknown> };
}

/**
 * Determines the appropriate image action and uploads the image if needed.
 * Handles both ADD and REPLACE actions for SMT entries.
 *
 * @param params - Configuration object containing data, entry ID, upload functions, and tracking utilities
 * @returns Promise that resolves when upload completes, or void if no upload is needed
 */
export const uploadSMTImage = async ({
  data,
  entryId,
  errorContext,
  existingEntry,
  logError,
  patientId,
  replaceSMTEntryImage,
  source,
  trackEvent,
  uploadSMTEntryImage,
}: UploadSMTImageParams): Promise<void> => {
  const imageAction = getSMTImageAction(data, existingEntry);

  if (
    imageAction.action !== SMTImageAction.ADD &&
    imageAction.action !== SMTImageAction.REPLACE
  ) {
    return;
  }

  const formData = await prepareSMTImageForUpload({
    data,
    errorContext,
    logError,
    source,
    trackEvent,
  });

  if (!formData) {
    return;
  }

  if (imageAction.action === SMTImageAction.ADD) {
    await uploadSMTEntryImage({
      formData,
      patientId,
      smtEntryId: entryId,
    }).unwrap();
  } else if (
    imageAction.action === SMTImageAction.REPLACE &&
    imageAction.existingImageId &&
    replaceSMTEntryImage
  ) {
    await replaceSMTEntryImage({
      formData,
      imageId: imageAction.existingImageId,
      patientId,
      smtEntryId: entryId,
    }).unwrap();
  }
};
